import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Pencil, Trash2 } from "lucide-react";
import {
  deleteCategory,
  fetchCategories,
} from "../../components/Manual_List/fetch/slices";
import { RootState } from "../../components/Manual_List/fetch/store";
import { Category, Language } from "../../Types/types";
import { useLanguage } from "../../components/LanguageContext";
import { useAppDispatch } from "../../components/Manual_List/fetch/hook";
import CircularProgress from "@mui/material/CircularProgress";
import { ConfirmationModal } from "../../components/shared/ConfrimationModal";
import translationManualList from "../../components/Manual_List/translationManualList";
import CustomPagination from "../../components/Manual_List/Pagination";
import ItemsPerPageSelect from "../../components/Manual_List/ItemsPerPage";
import SearchIcon from "@mui/icons-material/Search";
import { SelectChangeEvent } from "@mui/material/Select";
import translation from "./TranslationList";
import PackageListHeader from "../../components/Headers/PackageListHeader";
import { useNavigate } from "react-router-dom";
import { getLabel } from "../../utils/getLabel";

const CategoryList: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const categories: Category[] = useSelector(
    (state: RootState) => state.data.categories.data
  );
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();
  const effectiveLanguage = language || "";
  const ml = translationManualList[effectiveLanguage as Language];
  const t = translation[effectiveLanguage as Language];

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(
    null
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [page, setPage] = useState(1);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      await dispatch(fetchCategories(effectiveLanguage));
      setLoading(false);
    }
    fetchData();
  }, [dispatch, language, effectiveLanguage]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleItemsPerPageChange = (event: SelectChangeEvent<number>) => {
    setItemsPerPage(event.target.value as number);
    setPage(1);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const filteredCategories = categories.filter((category) => {
    const label = getLabel(category.name, language) || "";
    return label.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const pageCount = Math.ceil(filteredCategories.length / itemsPerPage);

  const handleDeleteCategory = (id: number) => {
    dispatch(deleteCategory(id));
    setIsConfirmationModalOpen(false);
  };

  const openConfirmationModal = (category: Category) => {
    setCategoryToDelete(category);
    setIsConfirmationModalOpen(true);
  };

  return (
    <div className="min-h-screen w-screen flex flex-col bg-gray-100 dark:bg-darkBackground dark:text-white">
      <PackageListHeader />
      <div className="flex flex-col items-center justify-start flex-grow relative">
        <div className="w-full px-4 sm:px-6 md:px-8 py-6 max-w-7xl flex flex-col">
          <h1 className="text-2xl font-semibold mb-6">{t.categoryList}</h1>

          <div className="flex flex-col sm:flex-row sm:justify-between items-center gap-4 mb-6 w-full">
            <div className="relative w-full sm:w-64">
              <input
                type="text"
                placeholder={ml.search}
                className="px-4 py-3 border border-gray-300 rounded-lg w-full pr-12 outline-none dark:bg-darkBackground dark:border-gray-600 focus:border-purple focus:ring-1 focus:ring-purple transition-colors shadow-sm"
                value={searchTerm}
                onChange={handleSearchChange}
              />
              <SearchIcon className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            </div>
            <button
              className="bg-purple text-white px-6 py-2.5 w-full sm:w-48 rounded-lg font-medium shadow-sm hover:bg-opacity-90 transition-all"
              onClick={() => navigate("/add-category")}
            >
              {t.addCategory}
            </button>
          </div>

          {filteredCategories.length > 0 && (
            <div className="overflow-x-auto rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <table className="min-w-full">
                <thead className="bg-gray-50 dark:bg-darkSecondaryBackground border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="py-4 px-6 font-semibold text-left text-sm text-gray-700 dark:text-gray-300">
                      {t.categoryName}
                    </th>
                    <th className="py-4 px-6 font-semibold text-right text-sm text-gray-700 dark:text-gray-300">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-darkSecondaryBackground divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredCategories
                    .slice((page - 1) * itemsPerPage, page * itemsPerPage)
                    .map((category) => (
                      <tr
                        key={category.id}
                        className="group hover:bg-purple hover:bg-opacity-5 dark:hover:bg-purple dark:hover:bg-opacity-10 transition-all duration-200 cursor-pointer"
                      >
                        <td className="py-4 px-6 text-left text-gray-900 dark:text-gray-100 font-medium">
                          {getLabel(category.name, language)}
                        </td>
                        <td className="py-4 px-6 text-right">
                          <div className="flex justify-end items-center space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/edit-category/${category.id}`);
                              }}
                              className="p-2 hover:bg-blue-50 dark:hover:bg-blue-900 dark:hover:bg-opacity-20 rounded-lg transition-all duration-200 group/edit"
                              title="Edit category"
                            >
                              <Pencil
                                className="text-gray-400 dark:text-gray-500 group-hover/edit:text-blue-600 dark:group-hover/edit:text-blue-400 group-hover/edit:scale-110 transition-all"
                                size={18}
                              />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openConfirmationModal(category);
                              }}
                              className="p-2 hover:bg-red-50 dark:hover:bg-red-900 dark:hover:bg-opacity-20 rounded-lg transition-all duration-200 group/delete"
                              title="Delete category"
                            >
                              <Trash2
                                className="text-gray-400 dark:text-gray-500 group-hover/delete:text-red-500 dark:group-hover/delete:text-red-400 group-hover/delete:scale-110 transition-all"
                                size={18}
                              />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          {filteredCategories.length === 0 && (
            <div className="text-center py-12 bg-white dark:bg-darkSecondaryBackground rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mt-2">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                />
              </svg>
              <p className="mt-4 text-lg font-medium text-gray-500 dark:text-gray-400">
                No categories found
              </p>
              <p className="mt-2 text-sm text-gray-400 dark:text-gray-500">
                Try adjusting your search or add a new category
              </p>
            </div>
          )}

          <div className="flex justify-between items-center pt-6 w-full">
            <CustomPagination
              page={page}
              pageCount={pageCount}
              onPageChange={handlePageChange}
            />
            <ItemsPerPageSelect
              itemsPerPage={itemsPerPage}
              handleItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        </div>
        {loading && (
          <div className="absolute inset-0 bg-gray-100 bg-opacity-75 flex justify-center items-center z-50">
            <CircularProgress />
          </div>
        )}
      </div>
      {categoryToDelete && (
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          onClose={() => setIsConfirmationModalOpen(false)}
          onConfirm={() => handleDeleteCategory(categoryToDelete.id)}
          label={t.deleteConfirmation(categoryToDelete.name)}
        />
      )}
    </div>
  );
};

export default CategoryList;
